// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/import-batch.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { importBatchesApi } from "../../api/import-batches";
import type { ImportBatch } from "../../api/types";
// override-block:imports BEGIN
import { createElement } from "react";
import type { PageHeaderAction } from "../../components/composition/PageHeader";
import type { ImportBatchStatus, ImportEntityType } from "../../api/types";
// override-block:imports END

// override-block:helpers BEGIN
const STATUS_LABEL: Record<ImportBatchStatus, string> = {
  pending: "Pending",
  mapping: "Mapping",
  validated: "Validated",
  executing: "Executing",
  completed: "Completed",
  failed: "Failed",
  canceled: "Canceled",
};
const ENTITY_TYPE_LABEL: Record<ImportEntityType, string> = {
  donor: "Donors",
  donation: "Donations",
  pledge: "Pledges",
  recurring_donation: "Recurring donations",
  prospect: "Prospects",
};
// override-block:helpers END

export const importBatchDetailConfig: DetailPageConfig<ImportBatch> = {
  idParam: "importBatchId",
  queryKey: (id: string) => ["import-batches", "detail", id] as const,
  queryFn: (id: string) => importBatchesApi.getImportBatch(id),
  backTo: "/imports",
  // override-block:title BEGIN
  // Title synthesis: "<EntityLabel> import — <StatusLabel>" (e.g. "Donors
  // import — Validated"). Hand-roll synthesized this because the bare
  // entity_type slug is operator-hostile.
  title: (b) =>
    `${ENTITY_TYPE_LABEL[b.entity_type] ?? b.entity_type} import — ${STATUS_LABEL[b.status] ?? b.status}`,
  // override-block:title END
  // override-block:subtitle BEGIN
  // Subtitle: source filename when present, otherwise the short id prefix.
  // (created_at-as-subtitle is the entity-hint default but it's a poor
  // descriptor for an import job — the upload filename is more identifying.)
  subtitle: (b) => b.source_filename ?? b.id.slice(0, 8) + "…",
  // override-block:subtitle END
  statusCards: (r, _fmt) => [
    // override-block:statcard-total BEGIN
    {
      id: "total",
      label: "Total rows",
      value: r.total_rows ?? 0,
      format: "number",
      tone: "g-home",
    },
    // override-block:statcard-total END
    // override-block:statcard-valid BEGIN
    {
      id: "valid",
      label: "Valid",
      value: r.valid_rows ?? 0,
      format: "number",
      tone: "g-giving",
    },
    // override-block:statcard-valid END
    // override-block:statcard-invalid BEGIN
    {
      id: "invalid",
      label: "Invalid",
      value: r.invalid_rows ?? 0,
      format: "number",
      tone: "g-events",
    },
    // override-block:statcard-invalid END
    // override-block:statcard-executed BEGIN
    {
      id: "executed",
      label: "Executed",
      value: r.executed_rows ?? 0,
      format: "number",
      tone: "g-mygiving",
    },
    // override-block:statcard-executed END
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    // header_actions are dynamic — gated by status per entity-hint
    // primary_actions[].when. The generator emits a fixed-array shape so
    // status-gating uses array spread + ternary inside the override block.
    // The parameter is `_r` per the template; reference it directly here.
    ...((_r.status === "pending" || _r.status === "mapping")
      ? [{ id: "validate", label: "Validate", variant: "secondary" } as PageHeaderAction]
      : []),
    ...(_r.status === "validated"
      ? [{ id: "execute", label: "Execute", variant: "primary" } as PageHeaderAction]
      : []),
    ...((_r.status !== "completed" && _r.status !== "failed" && _r.status !== "canceled")
      ? [{ id: "cancel", label: "Cancel", variant: "ghost" } as PageHeaderAction]
      : []),
    // override-block:actions END
  ],
  tabs: [
    // override-block:custom-tabs BEGIN
    // 4 tabs per import-batch-detail-v2.cross-screen.yaml §layout.tabs.
    // Errors / Mapping / Source are read-only renders backed by
    // /imports/{id}/{errors,mapping,rows}. For this CHG they show
    // informational placeholders pointing to the data source — full
    // per-tab rendering ships in a future polish CHG. The page is
    // functional + spec-traceable today.
    {
      id: "overview",
      label: "Overview",
      render: (b, fmt) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
          createElement("div", null, "Entity type: ", ENTITY_TYPE_LABEL[b.entity_type] ?? b.entity_type),
          createElement("div", null, "Status: ", STATUS_LABEL[b.status] ?? b.status),
          createElement("div", null, "Created: ", fmt.formatDate(b.created_at, "relative")),
          b.completed_at
            ? createElement("div", null, "Completed: ", fmt.formatDate(b.completed_at, "relative"))
            : null,
          b.source_filename
            ? createElement("div", null, "Source file: ", b.source_filename)
            : null,
          b.run_at
            ? createElement("div", null, "Scheduled run: ", fmt.formatDate(b.run_at, "medium"))
            : null,
          b.notify_email
            ? createElement("div", null, "Notify: ", b.notify_email)
            : null,
        ),
    },
    {
      id: "errors",
      label: "Errors",
      render: (b) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, color: "var(--color-text-muted)" } },
          `Per-row validation errors load from GET /imports/${b.id}/errors. Full table rendering ships in a follow-up CHG.`,
        ),
    },
    {
      id: "mapping",
      label: "Mapping",
      render: (b) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, color: "var(--color-text-muted)" } },
          `Saved column mapping loads from GET /imports/${b.id}/mapping. Full table rendering ships in a follow-up CHG.`,
        ),
    },
    {
      id: "source",
      label: "Source",
      render: (b) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, color: "var(--color-text-muted)" } },
          `Staged source rows load from GET /imports/${b.id}/rows. Full table rendering ships in a follow-up CHG.`,
        ),
    },
    // override-block:custom-tabs END
  ],
};
